import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, ShieldCheck } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { API_BASE_URL } from "../api/services";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  textMain: "#102A2A",
  textSub: "#4B5563",
  line: "#DDE7E5",
  surface: "#FFFFFF",
  bg: "#F4FBFA",
};

const Contact = () => {
 const [formData, setFormData] = useState({
  name: "",
  email: "",
  phone: "",
  city: "",
  message: "",
});  const [submitStatus, setSubmitStatus] = useState({ loading: false, success: false, error: null });
  const [countryCode, setCountryCode] = useState("+91");
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address.";
    }
    
    if (countryCode === "+91") {
      const phoneRegex = /^\d{10}$/;
      if (!formData.phone || !phoneRegex.test(formData.phone)) {
        errors.phone = "Please enter a valid 10-digit phone number.";
      }
    } else {
      const phoneRegex = /^\d{7,15}$/;
      if (!formData.phone || !phoneRegex.test(formData.phone)) {
        errors.phone = "Please enter a valid phone number (7 to 15 digits).";
      }
    }

    if (!formData.city || formData.city.trim().length < 2) {
      errors.city = "City must be at least 2 characters.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitStatus({ loading: true, success: false, error: null });
    try {
      // Using the centralized API_BASE_URL from api services
      const response = await fetch(`${API_BASE_URL}/contactus?t=${Date.now()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          phone: `${countryCode} ${formData.phone}`
        }),
      });

      if (response.ok) {
        setSubmitStatus({ loading: false, success: true, error: null });
        setFormData({ name: "", email: "", phone: "", city: "", message: "" });
        setCountryCode("+91");
      } else {
        const text = await response.text();
        setSubmitStatus({ loading: false, success: false, error: text || "Failed to submit enquiry" });
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus({ loading: false, success: false, error: "Network error occurred" });
    }
  };

  return (
    <section
      className="relative overflow-hidden px-4 sm:px-6 pt-10 sm:pt-16 pb-14 sm:pb-20"
      style={{
        background:
          "radial-gradient(circle at 12% 20%, rgba(58,175,169,0.14), transparent 34%), radial-gradient(circle at 88% 82%, rgba(123,200,164,0.17), transparent 35%), #F4FBFA",
      }}
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="mb-6 sm:mb-10 text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#3AAFA9]/40 bg-[#3AAFA9]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#2F8E89]">
            Trusted Support Channel
          </div>
          <h1
            className="mt-4 text-4xl font-extrabold sm:text-5xl"
            style={{ color: COLORS.textMain }}
          >
         Get in Touch
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm sm:text-base" style={{ color: COLORS.textSub }}>
           Let's discuss how JANAK can bring you peace of mind
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65 }}
          viewport={{ once: true }}
          className="grid overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 shadow-[0_28px_90px_rgba(16,42,42,0.12)] backdrop-blur-sm lg:grid-cols-[1.15fr_1fr]"
        >
          <div
            className="relative p-8 text-white sm:p-10 flex flex-col justify-between"
            style={{
              background: "linear-gradient(135deg, #5BC0B8, #8EDBD3)"  
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(58,175,169,0.4),transparent_35%),radial-gradient(circle_at_90%_90%,rgba(123,200,164,0.26),transparent_40%)] pointer-events-none" />
            <div className="relative z-10 flex flex-col h-full justify-between gap-8">
              <div>
                <h2 className="text-2xl font-bold sm:text-3xl tracking-tight">Premium Care Assistance</h2>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/90 font-medium">
                  Have questions about our verified care plans? Connect directly with our support team or fill in the secure consultation form.
                </p>
              </div>

              {/* Call, WhatsApp & Email Cards */}
              <div className="flex flex-col gap-4 w-full">
                
                {/* Phone Hotline Card */}
                <a
                  href="tel:+919098114568"
                  className="group flex items-center gap-4 rounded-2xl border border-white/20 bg-white/10 p-4 transition-all duration-300 hover:bg-white/20 hover:scale-[1.01] shadow-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white shrink-0 shadow-inner">
                    <Phone size={22} className="group-hover:animate-bounce" />
                  </div>
                  <div className="text-left">
                    <span className="text-[10px] text-white/70 uppercase tracking-wider font-bold block leading-none">Call Care Hotline</span>
                    <span className="text-xl sm:text-2xl font-black text-white mt-1.5 block leading-none tracking-tight">+91 90981 14568</span>
                    <span className="text-[10px] text-white/60 font-semibold mt-1 block">Mon-Sat: 9 AM - 8 PM</span>
                  </div>
                </a>

                {/* WhatsApp Chat Card */}
                <a
                  href="https://wa.me/919098114568?text=Hello%20JANAK%2C%20I%20have%20some%20questions%20about%20the%20care%20plans."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-2xl border border-white/20 bg-white/10 p-4 transition-all duration-300 hover:bg-white/20 hover:scale-[1.01] shadow-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/25 border border-emerald-500/30 flex items-center justify-center text-white shrink-0 shadow-inner">
                    <FaWhatsapp size={24} className="group-hover:rotate-[15deg] transition-transform duration-300" />
                  </div>
                  <div className="text-left">
                    <span className="text-[10px] text-white/70 uppercase tracking-wider font-bold block leading-none">WhatsApp Support</span>
                    <span className="text-lg sm:text-xl font-extrabold text-white mt-1.5 block leading-none">Chat in Real-Time</span>
                    <span className="text-[10px] text-white/60 font-semibold mt-1 block">Replies in under 5 minutes</span>
                  </div>
                </a>

                {/* Email Hotline Card */}
                <a
                  href="mailto:contact@thejanak.com"
                  className="group flex items-center gap-4 rounded-2xl border border-white/20 bg-white/10 p-4 transition-all duration-300 hover:bg-white/20 hover:scale-[1.01] shadow-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white shrink-0 shadow-inner">
                    <Mail size={20} />
                  </div>
                  <div className="text-left">
                    <span className="text-[10px] text-white/70 uppercase tracking-wider font-bold block leading-none">Email Consultation</span>
                    <span className="text-base sm:text-lg font-bold text-white mt-1.5 block leading-none">contact@thejanak.com</span>
                    <span className="text-[10px] text-white/60 font-semibold mt-1 block">Replies within 2 hours</span>
                  </div>
                </a>
              </div>

              {/* Bottom Support Footer Info */}
              <div className="flex items-center gap-2 text-white/80 text-xs font-semibold mt-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>Dedicated care support is active for registered members</span>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-7" style={{ background: COLORS.surface }}>
            <div className="mb-6">
              <h3 className="text-2xl font-semibold" style={{ color: COLORS.primary }}>
                Get In Touch
              </h3>
              <p className="mt-2 text-sm" style={{ color: COLORS.textSub }}>
                Fill in your details and we will reach out shortly.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Your Name */}
<div>
  <label
    className="mb-1.5 block text-sm font-semibold"
    style={{ color: COLORS.textMain }}
  >
    Your Name *
  </label>
  <input
    type="text"
    name="name"
    placeholder="Enter your full name"
    value={formData.name}
    onChange={handleChange}
    required
    maxLength="50"
    className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition"
    style={{ border: formErrors.name ? '1px solid #EF4444' : `1px solid ${COLORS.line}` }}
  />
  {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
</div>

{/* Email Address */}
<div>
  <label
    className="mb-1.5 block text-sm font-semibold"
    style={{ color: COLORS.textMain }}
  >
    Email Address *
  </label>
  <input
    type="email"
    name="email"
    placeholder="your.email@example.com"
    value={formData.email}
    onChange={handleChange}
    required
    maxLength="100"
    className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition"
    style={{ border: formErrors.email ? '1px solid #EF4444' : `1px solid ${COLORS.line}` }}
  />
  {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
</div>

{/* Phone Number */}
<div>
  <label
    className="mb-1.5 block text-sm font-semibold"
    style={{ color: COLORS.textMain }}
  >
    Phone Number *
  </label>
  <div className="flex gap-2">
    <select
      value={countryCode}
      onChange={(e) => setCountryCode(e.target.value)}
      className="rounded-lg px-2.5 py-2.5 text-sm outline-none bg-white border cursor-pointer shrink-0 transition"
      style={{ borderColor: COLORS.line, color: COLORS.textMain }}
    >
      <option value="+91">🇮🇳 +91</option>
      <option value="+1">🇺🇸 +1</option>
      <option value="+44">🇬🇧 +44</option>
      <option value="+61">🇦🇺 +61</option>
      <option value="+971">🇦🇪 +971</option>
      <option value="+966">🇸🇦 +966</option>
      <option value="+65">🇸🇬 +65</option>
      <option value="+60">🇲🇾 +60</option>
      <option value="+49">🇩🇪 +49</option>
      <option value="+33">🇫🇷 +33</option>
      <option value="+81">🇯🇵 +81</option>
      <option value="+82">🇰🇷 +82</option>
    </select>
    <input
      type="tel"
      name="phone"
      placeholder={countryCode === "+91" ? "10-digit number" : "Phone number"}
      value={formData.phone}
      onChange={(e) => {
        const value = e.target.value.replace(/\D/g, "");
        const maxLen = countryCode === "+91" ? 10 : 15;
        if (value.length <= maxLen) {
          setFormData({ ...formData, phone: value });
        }
      }}
      required
      className="flex-1 rounded-lg px-3 py-2.5 text-sm outline-none transition"
      style={{ border: formErrors.phone ? '1px solid #EF4444' : `1px solid ${COLORS.line}` }}
    />
  </div>
  {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
</div>

{/* Parents City */}
<div>
  <label
    className="mb-1.5 block text-sm font-semibold"
    style={{ color: COLORS.textMain }}
  >
    Parents' City *
  </label>
  <input
    type="text"
    name="city"
    placeholder="Which city do your parents live in?"
    value={formData.city}
    onChange={handleChange}
    required
    maxLength="50"
    className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition"
    style={{ border: formErrors.city ? '1px solid #EF4444' : `1px solid ${COLORS.line}` }}
  />
  {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
</div>

{/* Needs */}
<div>
  <label
    className="mb-1.5 block text-sm font-semibold"
    style={{ color: COLORS.textMain }}
  >
    Tell Us About Your Needs (Optional)
  </label>
  <textarea
    name="message"
    rows="4"
    placeholder="Any specific concerns or requirements you'd like to share?"
    value={formData.message}
    onChange={handleChange}
    maxLength="500"
    className="w-full resize-none rounded-xl px-4 py-3 text-sm outline-none transition"
    style={{ border: `1px solid ${COLORS.line}` }}
  />
</div>


             <button
  type="submit"
  disabled={submitStatus.loading}
  className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(91,192,184,0.35)] transition ${submitStatus.loading ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-0.5'}`}
  style={{
    background: "#3AAFA9",
  }}
>
  <ShieldCheck size={16} />
  {submitStatus.loading ? "Sending..." : "Send Secure Enquiry"}
</button>
{submitStatus.success && (
  <p className="mt-3 text-sm text-emerald-600 font-medium text-center">
    Thank you! Your enquiry has been submitted successfully.
  </p>
)}
{submitStatus.error && (
  <p className="mt-3 text-sm text-red-600 font-medium text-center">
    {submitStatus.error}
  </p>
)}
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
